import { Box, Divider, Layout, Text, TextButton } from '@wix/design-system';
import React, { useEffect, useMemo } from 'react';
import { dataHooks } from '../../../../utils/datahooks';
import { resolveCapabilitiesForEditorHost } from '../../../../utils/editor-capabilities-from-host';
import { RuleFooter } from '../../../rule-layout';
import { useAltTextFixerContext } from '../context/alt-text-fixer-context';
import { PreviewImage } from './preview-image';
const MAX_IMAGES = 4;

/**
 * Check if a status represents a "passed" state
 */
const isPassedStatus = status => {
  return status === 'valid' || status === 'fixed' || status === 'decorative';
};

/**
 * Props for the AltTextImageGrid component
 */

/**
 * See README.md for usage documentation.
 */
export const AltTextImageGrid = _ref => {
  let {
    images,
    taskStatus,
    openDrillInScreen,
    onClickCancel
  } = _ref;
  const {
    editorHost,
    biCallbacks,
    translations,
    imageUtils,
    designProps,
    wizardPresentation
  } = useAltTextFixerContext();

  // Preload images for better UX
  useEffect(() => {
    const preloadImages = () => {
      images.forEach(image => {
        const isExternal = (imageUtils.isExternalUrl == null ? void 0 : imageUtils.isExternalUrl(image.url)) ?? image.url.startsWith('http');
        if (isExternal) {
          const img = new Image();
          img.src = image.url;
          return;
        }
        // Preload both full-size and thumbnail
        const fullImg = new Image();
        fullImg.src = imageUtils.originalImageUrl(image.url);
        const thumbImg = new Image();
        thumbImg.src = imageUtils.resolveImageUrl(image.url, 32, 32);
      });
    };
    preloadImages();
  }, [images, imageUtils]);

  // Sort images: failed first, then passed
  const displayedImages = useMemo(() => {
    const failed = images.filter(img => !isPassedStatus(img.status));
    const passed = images.filter(img => isPassedStatus(img.status));
    return [...failed, ...passed].slice(0, MAX_IMAGES);
  }, [images]);
  const hasMore = images.length > MAX_IMAGES;
  const leftImagesCount = images.length - MAX_IMAGES;
  const passedCount = useMemo(() => images.filter(_ref2 => {
    let {
      status
    } = _ref2;
    return isPassedStatus(status);
  }).length, [images]);
  const failedCount = images.length - passedCount;
  const imagesGap = useMemo(() => {
    if (designProps != null && designProps.imagesGap) {
      return designProps.imagesGap;
    }
    return (wizardPresentation == null ? void 0 : wizardPresentation.imageGridGap) ?? resolveCapabilitiesForEditorHost(editorHost).wizardPresentation.imageGridGap;
  }, [designProps == null ? void 0 : designProps.imagesGap, editorHost, wizardPresentation == null ? void 0 : wizardPresentation.imageGridGap]);

  // Handle edit click (for passed state)
  const handleEdit = () => {
    biCallbacks.onEdit(images.length, failedCount);
    openDrillInScreen();
  };

  // Handle cancel click (for failed state)
  const handleCancel = () => {
    biCallbacks.onCancel(images.length, failedCount);
    onClickCancel();
  };

  // Handle get started click (for failed state)
  const handleGetStarted = () => {
    biCallbacks.onGetStarted(images.length, failedCount);
    openDrillInScreen();
  };
  return /*#__PURE__*/React.createElement(Box, {
    dataHook: dataHooks.altTextFixerWrapper,
    direction: "vertical",
    width: "100%"
  }, taskStatus && /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    marginBottom: "SP2",
    width: "100%"
  }, /*#__PURE__*/React.createElement(Divider, null)), /*#__PURE__*/React.createElement(Box, {
    align: "space-between",
    marginBottom: "SP2"
  }, /*#__PURE__*/React.createElement(Text, {
    dataHook: dataHooks.altTextFixerFilledText,
    size: "tiny",
    skin: "standard",
    weight: "thin"
  }, translations.filledProgress(passedCount, images.length)), taskStatus && /*#__PURE__*/React.createElement(TextButton, {
    dataHook: dataHooks.altTextFixerEditButton,
    size: "tiny",
    onClick: handleEdit
  }, translations.editButton)), /*#__PURE__*/React.createElement(Layout, {
    cols: MAX_IMAGES + 1,
    gap: imagesGap
  }, displayedImages.map(image => /*#__PURE__*/React.createElement(PreviewImage, {
    image: image,
    key: image.id
  })), hasMore && /*#__PURE__*/React.createElement(Box, {
    align: "center",
    border: "1px solid",
    borderColor: "D70",
    borderRadius: "4px",
    boxSizing: "border-box",
    dataHook: dataHooks.altTextFixerMoreImages,
    position: "relative",
    verticalAlign: "middle"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "small",
    skin: "standard",
    weight: "thin"
  }, `+${leftImagesCount}`))), !taskStatus && /*#__PURE__*/React.createElement(Box, {
    align: "right",
    marginTop: "SP2",
    width: "100%"
  }, /*#__PURE__*/React.createElement(RuleFooter, {
    dataHook: dataHooks.altTextFixerRuleFooter,
    design: designProps == null ? void 0 : designProps.ruleFooter,
    dismissButtonProps: {
      label: translations.cancelButton,
      onClick: handleCancel,
      type: 'button'
    },
    submitButtonProps: {
      label: translations.getStartedButton,
      onClick: handleGetStarted
    }
  })));
};
export default AltTextImageGrid;
//# sourceMappingURL=alt-text-image-grid.js.map